import useAccess from '@/composables/access/access';
import useGridPage from '@/composables/grid-page/grid-page';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePlannings from '@/composables/planning/plannings';
import useSpaceRouteContext from '@/composables/space/route-context';
import { GOAL_CHILDREN_LIST_VIEW_APPLICATION } from '@/lib/constants';
import { accessGroupFlag, accessPolicyType, viewApplication as viewApplicationConfig } from 'shared/constants.json';
import { computed } from 'vue';
import { publicViewApplications } from '@/lib/saved-view/saved-view';
import { useRoute } from 'vue-router';

export default function useViewsContext(viewApplication, readOnly = false) {
  const route = useRoute();
  const { spacesWithWriteAccessIds } = useLoggedInUser();
  const { userHasRight } = useAccess();
  const hasPublicViewAccess = computed(() => userHasRight([accessGroupFlag.publicSavedViews]));
  const gridPageSvc = useGridPage();
  const planningsSvc = usePlannings();
  const { routeSpaceId } = useSpaceRouteContext();

  const isPublic = computed(() => publicViewApplications.includes(viewApplication));

  const canEdit = computed(() => {
    if (readOnly) {
      return false;
    }

    if (viewApplication === viewApplicationConfig.memberSpace
      || [viewApplicationConfig.goalUserProfile, viewApplicationConfig.updateFeedProfile, viewApplicationConfig.updateUserProfile].includes(viewApplication)
      || viewApplication.includes('temp_goal_list')
      || viewApplication.includes('import_goals')
      || viewApplication === GOAL_CHILDREN_LIST_VIEW_APPLICATION
    ) {
      return false;
    }

    if (isPublic.value === false) {
      return true;
    }

    if (hasPublicViewAccess.value) {
      return true;
    }

    if ([viewApplicationConfig.planningDetails].includes(viewApplication)) {
      const planningId = parseInt(route.params.id, 10);
      return [accessPolicyType.write, accessPolicyType.full].includes(planningsSvc.selectSingle(planningId)?.accessRight);
    }

    if ([viewApplicationConfig.gridPage,
      viewApplicationConfig.gridPageTileGoal,
      viewApplicationConfig.gridPageTileUpdateFeed,
      viewApplicationConfig.gridPageTileMember,
      viewApplicationConfig.gridPageTileCheckin,
    ].includes(viewApplication)) {
      const gridPageId = parseInt(route.params.pageId, 10);
      return [accessPolicyType.write, accessPolicyType.full].includes(gridPageSvc.selectSingleGridPage(gridPageId)?.accessRight);
    }

    if ([viewApplicationConfig.goalSpace,
      viewApplicationConfig.updateSpace,
      viewApplicationConfig.updateFeedSpace,
      viewApplicationConfig.insightSpace,
      viewApplicationConfig.memberSpace,
    ].includes(viewApplication)) {
      return spacesWithWriteAccessIds.value.includes(routeSpaceId.value);
    }

    return false;
  });

  const ro = computed(() => readOnly);

  return { canEdit, isPublic, readOnly: ro };
}
